/*
 * Website Analytics — inline-SVG chart kit (spec §2 decision 5: one chart language,
 * colours only via --plk-* tokens). No chart library; the Inventory pattern.
 */
import React from "react";
import { num } from "../sales/territory-common";
const CUR = "var(--plk-chart-current)";
const PREV = "var(--plk-chart-previous)";
const ACCENT = "var(--plk-accent)";
export function Sparkline({
  points,
  height = 34,
  color = CUR
}) {
  const pts = (points || []).map(v => Number(v) || 0);
  if (pts.length < 2) return /*#__PURE__*/React.createElement("div", {
    style: {
      height
    }
  });
  const w = 200,
    h = height,
    min = Math.min(...pts),
    max = Math.max(...pts);
  const sx = w / (pts.length - 1);
  const sy = v => h - 3 - (v - min) / (max - min || 1) * (h - 8);
  const d = pts.map((v, i) => `${i ? "L" : "M"}${(i * sx).toFixed(1)} ${sy(v).toFixed(1)}`).join(" ");
  return /*#__PURE__*/React.createElement("svg", {
    className: "wa-spark",
    viewBox: `0 0 ${w} ${h}`,
    preserveAspectRatio: "none",
    "aria-hidden": "true",
    style: {
      height
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: `${d} L ${w} ${h} L 0 ${h} Z`,
    fill: color,
    opacity: ".14"
  }), /*#__PURE__*/React.createElement("path", {
    d: d,
    fill: "none",
    stroke: color,
    strokeWidth: "1.6"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: w,
    cy: sy(pts[pts.length - 1]).toFixed(1),
    r: "2.4",
    fill: color
  }));
}
function shortDate(v) {
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
}

// rows: [{date, value, marker}] · prev: number[] aligned by index (optional)
export function AreaChart({
  rows,
  prev,
  label = "value",
  height = 240,
  markerLabel = "conversions",
  prevLabel = "comparison"
}) {
  const [hover, setHover] = React.useState(null);
  const cur = (rows || []).map(r => Number(r.value) || 0);
  if (cur.length < 2) return /*#__PURE__*/React.createElement("div", {
    className: "wa-empty"
  }, "Not enough daily data for this range yet.");
  const W = 760,
    H = height,
    L = 46,
    R = 16,
    T = 16,
    B = 30;
  const max = Math.max(1, ...cur, ...(prev || [])) * 1.12;
  const n = cur.length;
  const sx = i => L + i * ((W - L - R) / (n - 1));
  const sy = v => T + (H - T - B) - v / max * (H - T - B);
  const path = arr => arr.map((v, i) => `${i ? "L" : "M"}${sx(i).toFixed(1)} ${sy(v).toFixed(1)}`).join(" ");
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(max * f));
  const step = Math.max(1, Math.ceil(n / 8));
  const labels = [];
  for (let i = 0; i < n; i += step) labels.push(i);
  if (labels[labels.length - 1] !== n - 1) labels.push(n - 1);
  const pk = cur.indexOf(Math.max(...cur));
  // Hover: nearest point to the pointer (viewBox units), crosshair + value box.
  const onMove = e => {
    const box = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - box.left) * (W / box.width);
    const i = Math.max(0, Math.min(n - 1, Math.round((x - L) / ((W - L - R) / (n - 1)))));
    if (i !== hover) setHover(i);
  };
  const hi = hover == null ? null : hover;
  const tipLines = hi == null ? [] : [`${shortDate(rows[hi].date)} · ${num(cur[hi])} ${label}`].concat(prev && prev.length === n ? [`${prevLabel}: ${num(prev[hi])}`] : []).concat(Number(rows[hi].marker) > 0 ? [`${num(rows[hi].marker)} ${markerLabel}`] : []);
  const tipW = Math.max(120, ...tipLines.map(t => t.length * 6.4 + 16));
  const tipX = hi == null ? 0 : Math.min(W - R - tipW, Math.max(L, sx(hi) - tipW / 2));
  const tipY = hi == null ? 0 : Math.max(T, sy(cur[hi]) - 14 - tipLines.length * 15);
  return /*#__PURE__*/React.createElement("svg", {
    className: "wa-chart",
    viewBox: `0 0 ${W} ${H}`,
    role: "img",
    "aria-label": `${label} per day`,
    onMouseMove: onMove,
    onMouseLeave: () => setHover(null),
    style: {
      cursor: "crosshair"
    }
  }, ticks.map(t => /*#__PURE__*/React.createElement("g", {
    key: t
  }, /*#__PURE__*/React.createElement("line", {
    x1: L,
    x2: W - R,
    y1: sy(t).toFixed(1),
    y2: sy(t).toFixed(1),
    stroke: "var(--plk-border-subtle)"
  }), /*#__PURE__*/React.createElement("text", {
    x: L - 8,
    y: (sy(t) + 4).toFixed(1),
    textAnchor: "end",
    fontSize: "11",
    fill: "var(--plk-text-subtle)"
  }, t >= 1000 ? `${(t / 1000).toFixed(t >= 10000 ? 0 : 1)}k` : t))), labels.map(i => /*#__PURE__*/React.createElement("text", {
    key: i,
    x: sx(i).toFixed(1),
    y: H - 10,
    textAnchor: i === n - 1 ? "end" : i === 0 ? "start" : "middle",
    fontSize: "11",
    fill: "var(--plk-text-subtle)"
  }, shortDate(rows[i].date))), /*#__PURE__*/React.createElement("path", {
    d: `${path(cur)} L ${sx(n - 1).toFixed(1)} ${sy(0)} L ${L} ${sy(0)} Z`,
    fill: CUR,
    opacity: ".14"
  }), prev && prev.length === n ? /*#__PURE__*/React.createElement("path", {
    d: path(prev),
    fill: "none",
    stroke: PREV,
    strokeWidth: "2",
    strokeDasharray: "4 4"
  }) : null, /*#__PURE__*/React.createElement("path", {
    d: path(cur),
    fill: "none",
    stroke: CUR,
    strokeWidth: "2.2",
    strokeLinejoin: "round"
  }), rows.map((r, i) => Number(r.marker) > 0 ? /*#__PURE__*/React.createElement("circle", {
    key: i,
    cx: sx(i).toFixed(1),
    cy: (sy(0) - 1).toFixed(1),
    r: (2 + Math.min(4, Number(r.marker) / 3)).toFixed(1),
    fill: ACCENT,
    opacity: ".9"
  }, /*#__PURE__*/React.createElement("title", null, `${shortDate(r.date)} · ${num(r.marker)} ${markerLabel}`)) : null), hi == null ? /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("circle", {
    cx: sx(pk).toFixed(1),
    cy: sy(cur[pk]).toFixed(1),
    r: "3.5",
    fill: CUR
  }), /*#__PURE__*/React.createElement("text", {
    x: sx(pk).toFixed(1),
    y: (sy(cur[pk]) - 9).toFixed(1),
    textAnchor: "middle",
    fontSize: "11",
    fontWeight: "700",
    fill: "var(--plk-text)"
  }, num(cur[pk]))) : /*#__PURE__*/React.createElement("g", {
    style: {
      pointerEvents: "none"
    }
  }, /*#__PURE__*/React.createElement("line", {
    x1: sx(hi).toFixed(1),
    x2: sx(hi).toFixed(1),
    y1: T,
    y2: sy(0),
    stroke: "var(--plk-text-subtle)",
    strokeDasharray: "3 3"
  }), prev && prev.length === n ? /*#__PURE__*/React.createElement("circle", {
    cx: sx(hi).toFixed(1),
    cy: sy(prev[hi]).toFixed(1),
    r: "3.5",
    fill: "var(--plk-surface)",
    stroke: PREV,
    strokeWidth: "2"
  }) : null, /*#__PURE__*/React.createElement("circle", {
    cx: sx(hi).toFixed(1),
    cy: sy(cur[hi]).toFixed(1),
    r: "4.5",
    fill: "var(--plk-surface)",
    stroke: CUR,
    strokeWidth: "2.5"
  }), /*#__PURE__*/React.createElement("rect", {
    x: tipX.toFixed(1),
    y: tipY.toFixed(1),
    width: tipW.toFixed(1),
    height: tipLines.length * 15 + 8,
    rx: "6",
    fill: "var(--plk-text)",
    opacity: ".92"
  }), tipLines.map((t, k) => /*#__PURE__*/React.createElement("text", {
    key: k,
    x: (tipX + 8).toFixed(1),
    y: (tipY + 15 + k * 15).toFixed(1),
    fontSize: "11",
    fontWeight: k === 0 ? "700" : "500",
    fill: "var(--plk-surface)"
  }, t))));
}

// rows: [{name, value, sub, right}] — horizontal bars with a right-hand cell
export function HBars({
  rows,
  head = ["", "", "Sessions", ""],
  accentFirst = false,
  onClick,
  cls = ""
}) {
  const max = Math.max(1, ...(rows || []).map(r => Number(r.value) || 0));
  const total = (rows || []).reduce((a, r) => a + (Number(r.value) || 0), 0);
  if (!rows || !rows.length) return /*#__PURE__*/React.createElement("div", {
    className: "wa-empty"
  }, "Nothing in this range.");
  return /*#__PURE__*/React.createElement("div", {
    className: `wa-hbars ${cls}`
  }, /*#__PURE__*/React.createElement("div", {
    className: "row head"
  }, /*#__PURE__*/React.createElement("span", null, head[0]), /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("span", {
    className: "n"
  }, head[2]), /*#__PURE__*/React.createElement("span", {
    className: "n"
  }, head[3])), rows.map((r, i) => /*#__PURE__*/React.createElement("div", {
    className: "row" + (onClick ? " clickable" : ""),
    key: r.key != null ? r.key : `${r.name}|${i}`,
    onClick: onClick ? () => onClick(r) : undefined
  }, /*#__PURE__*/React.createElement("span", {
    className: "name",
    title: r.name
  }, r.name), /*#__PURE__*/React.createElement("span", {
    className: "track"
  }, /*#__PURE__*/React.createElement("i", {
    className: accentFirst && i === 0 ? "acc" : "",
    style: {
      width: `${((Number(r.value) || 0) / max * 100).toFixed(1)}%`
    }
  })), /*#__PURE__*/React.createElement("span", {
    className: "n"
  }, num(r.value), " ", /*#__PURE__*/React.createElement("small", null, total ? `${((Number(r.value) || 0) / total * 100).toFixed(0)}%` : "")), /*#__PURE__*/React.createElement("span", {
    className: "n"
  }, r.right == null ? "" : r.right))));
}

// Delta chip: {pct, dir} or null
// d = deltaOf() result {dir, pct} · d.unit = "pp" renders percentage points · invert = down is good
export function Delta({
  d,
  invert = false
}) {
  if (!d || d.pct == null || !isFinite(d.pct)) return /*#__PURE__*/React.createElement("span", {
    className: "wa-d flat"
  }, "no comparison");
  const dir = invert ? d.dir === "up" ? "down" : d.dir === "down" ? "up" : "flat" : d.dir;
  const pct = Number(d.pct) || 0;
  return /*#__PURE__*/React.createElement("span", {
    className: `wa-d ${dir}`
  }, pct > 0 ? "+" : "", d.unit === "pp" ? `${pct.toFixed(2)} pp` : `${pct.toFixed(1)}%`);
}